import React, { useState, useCallback } from "react";
import {
    Box,
    Typography,
    Paper,
    Button,
    IconButton,
    Tooltip,
    Snackbar,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    CircularProgress,
    Fab,
    TablePagination,
    TextField,
    InputAdornment
} from "@mui/material";
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon } from "@mui/icons-material";
import DocumentsTypeForm from "./DocumentsTypeForm";
import { useDocumentsType } from "../../hooks/useDocumentsType";
import { useAuthContext } from "../../context/AuthProvider";

const DocumentsTypeDashboard = () => {
    const [formState, setFormState] = useState({ open: false, documentType: null });
    const [alert, setAlert] = useState({ open: false, message: "", severity: "success" });
    const [confirmDialog, setConfirmDialog] = useState({ open: false, documentType: null });

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState("");

    const { user } = useAuthContext();
    const { documentsType, isLoading, error, createMutation, updateMutation, deleteMutation } = useDocumentsType();

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const filteredDocuments = documentsType?.filter((type) =>
        type.doctip.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (type.docdes && type.docdes.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    const showAlert = (message, severity = "success") => {
        setAlert({ open: true, message, severity });
    };

    const handleOpenForm = (documentType = null) => {
        setFormState({ open: true, documentType });
    };

    const handleCloseForm = () => {
        setFormState({ open: false, documentType: null });
    };

    const handleSubmitDocumentType = useCallback((data) => {
        if (!user) {
            showAlert("Debe iniciar sesión para realizar esta acción", "error");
            return;
        }

        if (formState.documentType) {
            updateMutation.mutate(
                { id: formState.documentType.doctipid, data },
                {
                    onSuccess: () => {
                        showAlert("Tipo de documento actualizado con éxito");
                        handleCloseForm();
                    },
                    onError: (error) => {
                        showAlert(error.message || "Error al actualizar el tipo de documento", "error");
                    },
                }
            );
        } else {
            createMutation.mutate(data, {
                onSuccess: () => {
                    showAlert("Tipo de documento creado con éxito");
                    handleCloseForm();
                },
                onError: (error) => {
                    showAlert(error.message || "Error al crear el tipo de documento", "error");
                },
            });
        }
    }, [user, formState.documentType, createMutation, updateMutation]);

    const handleConfirmDelete = () => {
        if (confirmDialog.documentType) {
            deleteMutation.mutate(confirmDialog.documentType.doctipid, {
                onSuccess: () => {
                    showAlert("Tipo de Documento eliminado con éxito");
                    setConfirmDialog({ open: false, documentType: null });
                },
                onError: (error) => {
                    showAlert(error.response?.data?.error || "Error al eliminar el tipo de documento", "error");
                },
            });
        }
    };

    return (
        <Paper sx={{ p: 3, backgroundColor: "#fafafa", borderRadius: 2 }}>
            {/*<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5" fontWeight={600}>Tipos de Documentos</Typography>
            </Box>*/}

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5" fontWeight={600}>Tipos de Documentos</Typography>

                <TextField
                    variant="outlined"
                    size="small"
                    placeholder="Buscar..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>

            {isLoading ? (
                <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
            ) : error ? (
                <Typography color="error">Error al cargar los tipos de documentos: {error.message}</Typography>
            ) : (
                <TableContainer component={Paper} elevation={3}>
                    <Table>
                        <TableHead sx={{ backgroundColor: "#e0e0e0" }}>
                            <TableRow>
                                <TableCell><strong>Nombre</strong></TableCell>
                                <TableCell><strong>Descripción</strong></TableCell>
                                <TableCell align="center"><strong>Acciones</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filteredDocuments.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((type) => (
                                <TableRow key={type.doctipid} hover>
                                    <TableCell>{type.doctip}</TableCell>
                                    <TableCell>{type.docdes || "Sin descripción"}</TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Editar">
                                            <IconButton size="small" onClick={() => handleOpenForm(type)}>
                                                <EditIcon color="primary" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Eliminar">
                                            <IconButton size="small" onClick={() => setConfirmDialog({ open: true, documentType: type })}>
                                                <DeleteIcon color="error" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    {/* Paginación */}
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={filteredDocuments.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                </TableContainer>                               
            )}

            {/* Botón flotante para agregar nuevo tipo de documento */}
            <Fab color="primary" aria-label="add" sx={{ position: "fixed", bottom: 20, right: 20 }} onClick={() => handleOpenForm()}>
                <AddIcon />
            </Fab>

            {/* Formulario Modal */}
            {formState.open && (
                <DocumentsTypeForm
                    open={formState.open}
                    handleClose={handleCloseForm}
                    onSubmit={handleSubmitDocumentType}
                    defaultValues={formState.documentType || {}}
                />
            )}

            {/* Diálogo de Confirmación */}
            <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, documentType: null })}>
                <DialogTitle>Confirmar Eliminación</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Está seguro de que desea eliminar el tipo de documento "{confirmDialog.documentType?.doctip}"? Esta acción no se puede deshacer.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDialog({ open: false, documentType: null })} color="secondary">Cancelar</Button>
                    <Button onClick={handleConfirmDelete} variant="contained" color="error">Eliminar</Button>
                </DialogActions>
            </Dialog>

            {/* Notificaciones */}
            <Snackbar open={alert.open} autoHideDuration={4000} onClose={() => setAlert({ ...alert, open: false })}>
                <Alert severity={alert.severity}>{alert.message}</Alert>
            </Snackbar>
        </Paper>
    );
};

export default DocumentsTypeDashboard;
